// src/components/ScorecardHistory.js
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Divider
} from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../firebase-config";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import FullScorecardTable from "../Scorecard/FullScorecardTable"; // Adjust path as needed
import NineHoleScorecardTable from "../Scorecard/NineHoleScorecardTable"; // Adjust path as needed

const ScorecardHistory = () => {
  const { user } = useAuth();
  const [scorecards, setScorecards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    const scorecardsRef = collection(db, "users", user.uid, "scorecards");
    const q = query(scorecardsRef, orderBy("roundDate", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const rounds = [];
        querySnapshot.forEach((doc) => {
          rounds.push({ id: doc.id, ...doc.data() });
        });
        setScorecards(rounds);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching scorecards:", err);
        setError("Failed to fetch scorecards.");
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [user]);

  const formatRoundDate = (roundDate) => {
    if (!roundDate) return "Date not available";
    const dateObj =
      typeof roundDate === "object" && roundDate.seconds
        ? new Date(roundDate.seconds * 1000)
        : new Date(roundDate);
    return dateObj.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  if (!user) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography variant="h6">
          Please log in to view your scorecard history.
        </Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Round History
      </Typography>
      {loading ? (
        <Grid container justifyContent="center">
          <CircularProgress />
        </Grid>
      ) : error ? (
        <Typography variant="body1" color="error">
          {error}
        </Typography>
      ) : scorecards.length === 0 ? (
        <Typography variant="body1">No scorecards found.</Typography>
      ) : (
        <Grid container spacing={2}>
          {scorecards.map((record) => {
            // Determine if the round is 9-hole. Default to 18 if not provided.
            const numHoles = record.numHoles ? parseInt(record.numHoles, 10) : 18;
            const nineChoice = record.nineChoice ? record.nineChoice : "Front";
            // For 9-hole rounds with nineChoice "Back", start at index 9; otherwise, index 0.
            const offset = numHoles === 9 && nineChoice === "Back" ? 9 : 0;
            const holesDisplayed = numHoles === 9 ? 9 : 18;

            // Extract scores from record.scores (an array of objects)
            const scoresArray = record.scores
              ? record.scores
                  .slice(offset, offset + holesDisplayed)
                  .map((s) => s.score)
              : [];

            // Construct a selectedTee object from stored teeData and selectedTee.
            const selectedTeeObj =
              record.teeData && record.selectedTee
                ? {
                    tee_name: record.selectedTee,
                    holes:
                      record.teeData[record.selectedTee] &&
                      Array.isArray(record.teeData[record.selectedTee].yardages)
                        ? record.teeData[record.selectedTee].yardages
                            .slice(offset, offset + holesDisplayed)
                            .map((yardage, index) => ({
                              yardage,
                              par:
                                record.scores && record.scores[offset + index]
                                  ? record.scores[offset + index].par
                                  : 0,
                              handicap:
                                record.scores && record.scores[offset + index]
                                  ? record.scores[offset + index].handicap
                                  : "",
                              hole_number: offset + index + 1
                            }))
                        : []
                  }
                : null;

            // Compute overall totals (from the slice of scores used).
            const totalPar = record.scores
              ? record.scores
                  .slice(offset, offset + holesDisplayed)
                  .reduce((sum, h) => sum + (h.par || 0), 0)
              : 0;
            const totalScore = record.scores
              ? record.scores
                  .slice(offset, offset + holesDisplayed)
                  .reduce((sum, h) => sum + (h.score || 0), 0)
              : 0;
            const diff = totalScore - totalPar;

            return (
              <Grid item xs={12} md={6} key={record.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {record.clubName || "Unknown Club"} - {formatRoundDate(record.roundDate)}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Course: {record.courseName || "N/A"}
                    </Typography>
                    {/* Render 9-hole or 18-hole scorecard */}
                    {numHoles === 9 ? (
                      <NineHoleScorecardTable
                        readOnly={true}
                        scores={scoresArray}
                        selectedTee={selectedTeeObj}
                        holeOffset={offset}
                      />
                    ) : (
                      <FullScorecardTable
                        readOnly={true}
                        scores={scoresArray}
                        selectedTee={selectedTeeObj}
                      />
                    )}
                    <Divider sx={{ my: 1 }} />
                    {/* Additional details from the scorecard */}
                    {record.course_rating !== undefined && (
                      <Typography variant="body2">
                        Course Rating: {record.course_rating}
                      </Typography>
                    )}
                    {record.slope_rating !== undefined && (
                      <Typography variant="body2">
                        Slope Rating: {record.slope_rating}
                      </Typography>
                    )}
                    {record.differential !== undefined && (
                      <Typography variant="body2">
                        Differential: {Number(record.differential).toFixed(2)}
                      </Typography>
                    )}
                    <Typography variant="body2">
                      Score Difference: {diff >= 0 ? `+${diff}` : diff}
                    </Typography>
                    {record.submittedAt && record.submittedAt.seconds && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ display: "block", mt: 1 }}
                      >
                        Submitted: {new Date(record.submittedAt.seconds * 1000).toLocaleString()}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Container>
  );
};

export default ScorecardHistory;